import { EventType } from "@/analytics/analytics-events";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ModalSpinner } from "@/components/ui/modal-spinner";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { changeMode } from "@/store/mode-slice";
import {
  selectControlPointsAlignmentAnchorPositions,
  selectControlPointsSheetElevation,
  selectElementToAlignWithControlPointsAlignment,
} from "@/store/modes/control-points-alignment-mode-selectors";
import { resetControlPointsAlignment } from "@/store/modes/control-points-alignment-mode-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import {
  ProjectApiPose,
  selectAncestor,
  selectIElement,
  selectIElementProjectApiLocalPose,
  ViewDiv,
} from "@faro-lotv/app-component-toolbox";
import { grey, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  isIElementAreaSection,
  isIElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import { Mutation } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useCallback, useState, type JSX } from "react";
import {
  AlignmentElementLabel,
  LabelType,
} from "../alignment-modes-commons/alignment-element-label";
import {
  AlignmentTransform,
  alignmentTransformToMatrix4,
  IDENTITY,
  matrix4ToAlignmentTransform,
} from "../alignment-modes-commons/alignment-transform";
import { computeSplitScreenAlignment } from "../alignment-modes-commons/compute-split-screen-alignment";
import {
  createAreaAlignmentMutations,
  createSheetAlignmentMutations,
} from "../alignment-modes-commons/project-alignment-mutations";
import { useOverlayElements, useOverlayRef } from "../overlay-elements-context";
import { ControlPointsAlignmentProgressBar } from "./control-points-alignment-progress-bar";
import { ControlPointsAlignmentSetPointsPanel } from "./control-points-alignment-set-points-panel";

/** @returns The overlay for control-points alignment mode */
export function ControlPointsAlignmentModeOverlay(): JSX.Element {
  const [showSpinner, setShowSpinner] = useState(false);
  const store = useAppStore();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();
  const client = useCurrentProjectApiClient();
  const { handleErrorWithToast } = useErrorHandlers();

  const elementToAlignId = useAppSelector(
    selectElementToAlignWithControlPointsAlignment,
  );

  const elementToAlign = useAppSelector(selectIElement(elementToAlignId));
  assert(elementToAlignId && elementToAlign, "Element to align not assigned");

  assert(
    isIElementAreaSection(elementToAlign) ||
      isIElementGenericImgSheet(elementToAlign),
    "Control points alignment is supported at the moment only for areas and sheets",
  );

  const applyAreaMutation = useCallback(async () => {
    setShowSpinner(true);

    const elevation = selectControlPointsSheetElevation(store.getState()) ?? 0;
    const alignmentAnchorPositions =
      selectControlPointsAlignmentAnchorPositions(store.getState());

    const computed2PairsTransform =
      computeSplitScreenAlignment(alignmentAnchorPositions, true) ?? IDENTITY;

    const computedTransformMatrix = alignmentTransformToMatrix4(
      computed2PairsTransform,
    );

    const area = selectAncestor(
      elementToAlign,
      isIElementAreaSection,
    )(store.getState());

    assert(area, "Invalid area");

    const sectionAreaWorldMatrix = selectIElementWorldMatrix4(area.id)(
      store.getState(),
    );

    const targetWorldMatrix = isIElementAreaSection(elementToAlign)
      ? sectionAreaWorldMatrix
      : selectIElementWorldMatrix4(elementToAlign.id)(store.getState());

    const finalTransform = matrix4ToAlignmentTransform(
      computedTransformMatrix.multiply(targetWorldMatrix),
    );

    //  apply elevation from user input to the final transform
    finalTransform.position[1] = elevation;

    let mutations: Mutation[] = [];

    if (isIElementAreaSection(elementToAlign)) {
      const areaLocalTransform = selectIElementProjectApiLocalPose(
        elementToAlign,
        alignmentTransformToMatrix4(finalTransform),
      )(store.getState());

      const transform = projectApiPoseToAlignmentTransform(areaLocalTransform);
      mutations = createAreaAlignmentMutations(elementToAlign, transform);
    } else {
      const sheetLocalMatrix = sectionAreaWorldMatrix
        .invert()
        .multiply(alignmentTransformToMatrix4(finalTransform));

      const localTransform = selectIElementProjectApiLocalPose(
        elementToAlign,
        sheetLocalMatrix,
      )(store.getState());

      const transform = projectApiPoseToAlignmentTransform(localTransform);
      mutations = createSheetAlignmentMutations(elementToAlign, transform);
    }

    Analytics.track(EventType.confirmControlPointsAlignmentArea);

    try {
      await client.applyMutations(mutations);
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to save new alignment",
        error,
      });

      setShowSpinner(false);
      return;
    }

    // at the end of alignment cycle reset temporary data to prevent reusing it in the next session of alignment
    dispatch(resetControlPointsAlignment());

    // Fetch the changed section area sub-tree and update the local copy of the project
    // that new alignment will be used without reloading whole project
    dispatch(
      updateProject({
        projectApi: client,
        iElementQuery: {
          // Refresh the area node to get new transform
          ancestorIds: [elementToAlign.id],
        },
      }),
    );

    setShowSpinner(false);

    openToast({
      title: "Alignment completed",
      variant: "success",
    });

    // after alignment force switch to 2D mode
    dispatch(changeMode("sheet"));
  }, [
    client,
    dispatch,
    handleErrorWithToast,
    openToast,
    elementToAlign,
    store,
  ]);

  const { setSingleScreen } = useOverlayElements();
  const singleScreenRef = useOverlayRef(setSingleScreen);

  return (
    <>
      <ModalSpinner
        sx={{ color: grey[0], zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showSpinner}
      />
      <Stack
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
        }}
      >
        <ControlPointsAlignmentProgressBar apply={applyAreaMutation} />

        <Stack direction="row" sx={{ height: "100%" }}>
          <ViewDiv
            eventDivRef={singleScreenRef}
            sx={{
              height: "100%",
              width: "100%",
              borderRight: 3,
              borderRightColor: grey[200],
            }}
          >
            <AlignmentElementLabel
              element={elementToAlign}
              labelType={LabelType.alignedElement}
              sx={{
                top: "10px",
                left: "30px",
              }}
            />
          </ViewDiv>
          <ControlPointsAlignmentSetPointsPanel
            isArea={isIElementAreaSection(elementToAlign)}
          />
        </Stack>
      </Stack>
    </>
  );
}

/**
 *  Converts a ProjectApiPose to an AlignmentTransform
 *
 * @param pose The Project API pose to convert
 * @returns The converted AlignmentTransform
 */
function projectApiPoseToAlignmentTransform(
  pose: ProjectApiPose,
): AlignmentTransform {
  return {
    position: [pose.pos.x, pose.pos.y, pose.pos.z],
    quaternion: [pose.rot.x, pose.rot.y, pose.rot.z, pose.rot.w],
    scale: [pose.scale.x, pose.scale.y, pose.scale.z],
  };
}
